import React from 'react';
import type { FC } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { routes } from '../constants';
import { getCustomerTags } from '../services/utils/tags';
import { ReservationModel, ReservationTagModel, ReservationTagType } from '@/models/reservation';
import { TagProps, TagType as TagComponentType } from './Tag';
import Tags from './Tags';
import TitleLink from './TitleLink';
import ReservationNotesCard from './ReservationNotesCard';
import CustomerNotes from './CustomerNotes';
import Title from './typography/Title';
import ReservationDates from './ReservationDates';
import { Content, Edge, NotesContainerGrid, ReservationDetailsGrid, StyledCard } from './ReservationCard.styled';
import { TagType } from '@/models/tag';

interface Props {
  readonly reservation: ReservationModel;
}

function generateTagComponentType(tagType: ReservationTagType) : TagComponentType {
  switch (tagType) {
    case ReservationTagType.PAID:
    case ReservationTagType.ARRIVED: {
      return TagComponentType.SUCCESS;
    }

    case ReservationTagType.OVERPAID:
    case ReservationTagType.NOT_ARRIVED: {
      return TagComponentType.WARNING;
    }

    case ReservationTagType.UNPAID: {
      return TagComponentType.DANGER;
    }

    default: {
      return TagComponentType.COMMON;
    }
  }
}

function generateReservationTags(tags: ReservationTagModel[] = []) : TagProps[] {
  return tags.map(({ type, label }) => ({
    type: generateTagComponentType(type),
    label: label
  }));
}

const ReservationCard : FC<Props> = ({ reservation }) => {
  const { id, confirmationNumber, startDate, endDate, customer, property, reservationNotes, source } = reservation;
  const { firstName, lastName, customerNotes } = customer!;
  const propertyName = property!.name;
  const title = `${firstName} ${lastName} / ${confirmationNumber}`;
  const reservationLink = routes.reservation(id);
  const tags : TagProps[] = [];
  const customerClassificationTags = getCustomerTags(customer!, TagType.CLASSIFICATION);

  const classificationTags : TagProps[] = customerClassificationTags.map(tag => ({
    label: tag
  }));

  const reservationTags: TagProps[] = generateReservationTags(reservation.reservationTags).sort(
    (tagA, tagB) => tagA.label.localeCompare(tagB.label)
  );

  tags.push(
    ...reservationTags,
    ...classificationTags
  );

  return (
    <StyledCard data-id={id}>
      <Edge />

      <Content container spacing={3} justifyContent="space-between">
        <Grid container item sm={6} xs={12} spacing={2} direction="column">
          <Grid item>
            <TitleLink href={reservationLink}>
              { title }
            </TitleLink>
          </Grid>

          <Grid item>
            <Tags tags={tags} />
          </Grid>
        </Grid>

        <ReservationDetailsGrid item container direction="column" sm={6} xs={12}>
          <Grid item>
            <Title>
              { propertyName }
            </Title>
          </Grid>

          <Grid item>
            <Typography variant="subtitle1">
              <ReservationDates
                formatter="dd.MM / HH:mm"
                startDate={startDate}
                endDate={endDate}
              />
            </Typography>
          </Grid>

          <Grid item>
            <Typography variant="subtitle1">
              { source }
            </Typography>
          </Grid>
        </ReservationDetailsGrid>

        <NotesContainerGrid item>
          <CustomerNotes notes={customerNotes} />
          <ReservationNotesCard notes={reservationNotes} />
        </NotesContainerGrid>
      </Content>
    </StyledCard>
  );
};

export default ReservationCard;
